import React from 'react';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import classNames from 'classnames';
import eventBus from 'general/js/event-bus';
import app,
{
    EVENT_DEVICE_TYPE_CHANGED,
    DEVICE_TYPE_DESKTOP,
    DEVICE_TYPE_DESKTOP_WIDE,
} from 'general/js/app';
import { favoritePlayerService } from 'general/js/services';
import {
    EVENT_FAVOURITE_PLAYER_SERVICE_ADD,
    EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE
} from 'general/js/services/favorite-player-services';
import {
    generateBemCss,
    isRoundChoicesEqual,
    isRoundChoiceAvailable,
    setSelectedChoice
} from 'general/js/helpers';
import gtm from 'general/js/gtm';
import { LazyImage } from 'project/lazy-image';
import { LiveEventSelect } from 'components/live-event-select';
import { LivePreMessage } from 'components/live-pre-message';
import {
    SearchAutocomplete,
    getSuggestionList,
    getDefaultCallback,
} from 'components/search-autocomplete';
import { PlayerSuggestion, CountrySuggestion } from 'components/search-suggestions';
import { FavoriteButton } from 'components/favorite-button';
import LeaderboardHbhButton from './components/leaderboard-hbh-button';
import LeaderboardTableWrapper from './containers/leaderboard-table-wrapper';
import LeaderboardInfoButton from './components/leaderboard-info-button';
import { sortingStrategies } from './components/sortingStrategies';
import { splitPlayers } from './actions/get-players-data';
import { viewTypes, cellTypes, noFavoriteMessage } from './enums';

const SUBLAYOUT_FULLWIDTH_ENABLE = 'SUBLAYOUT_FULLWIDTH_ENABLE';
const SUBLAYOUT_FULLWIDTH_DISABLE = 'SUBLAYOUT_FULLWIDTH_DISABLE';

class LeaderboardContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            view: viewTypes.SUMMARY,
            roundChoices: null,
            selectedRoundId: null,
            favoritePlayerList: favoritePlayerService.getAll(),
            sortingType: cellTypes.POSITION,
            isSortingAscending: true,
            isPredictive: false,
            predictiveSearchResults: [],
            applyFavourite: false,
            notify: true
        };
    }

    componentDidMount() {
        eventBus.addListener(EVENT_FAVOURITE_PLAYER_SERVICE_ADD, this.renderFavoritePlayers);
        eventBus.addListener(EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE, this.renderFavoritePlayers);
        eventBus.addListener(EVENT_DEVICE_TYPE_CHANGED, this.changedRenderedViews);
        this.updateChoices();
    }

    componentDidUpdate() {
        this.updateChoices();
    }

    componentWillUnmount() {
        eventBus.removeListener(EVENT_FAVOURITE_PLAYER_SERVICE_ADD, this.renderFavoritePlayers);
        eventBus.removeListener(EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE, this.renderFavoritePlayers);
        eventBus.removeListener(EVENT_DEVICE_TYPE_CHANGED, this.changedRenderedViews);
    }

    updateChoices() {
        const { liveScanner } = this.props;
        const { selectedRoundId } = this.state;

        if (!liveScanner) return;

        const prevChoices = this.state.roundChoices;
        const roundChoices = get(liveScanner, 'liveComponents.leaderboard.roundChoices');

        if (this.state.selectedRoundId &&
            isRoundChoiceAvailable(roundChoices, selectedRoundId)
        ) {
            setSelectedChoice(cloneDeep(roundChoices), selectedRoundId);

            if (!isRoundChoicesEqual(prevChoices, roundChoices)) {
                this.setState({ roundChoices });
            }
            return;
        }

        if (isRoundChoicesEqual(prevChoices, roundChoices)) return;

        this.setState({
            roundChoices,
            selectedRoundId: null,
        });
    }

    toggleHoleByHoleView = () => {
        let view = viewTypes.HOLE_BY_HOLE;

        if (this.state.view === viewTypes.HOLE_BY_HOLE) {
            view = viewTypes.SUMMARY;
            eventBus.emit(SUBLAYOUT_FULLWIDTH_DISABLE);
        } else {
            eventBus.emit(SUBLAYOUT_FULLWIDTH_ENABLE);
            gtm.push('Leaderboard', 'Hole by Hole', 'Select Hole by Hole');
        }

        this.setState({ view, selectedRoundId: this.state.currentRound });
    }

    changedRenderedViews = () => {
        if (!(app.deviceTypeByViewport === DEVICE_TYPE_DESKTOP ||
            app.deviceTypeByViewport === DEVICE_TYPE_DESKTOP_WIDE) &&
            this.state.view !== viewTypes.HOLE_BY_HOLE
        ) {
            this.setState({
                view: viewTypes.SUMMARY,
            });
        }
    }

    toggleRScoreView = () => {
        let view = viewTypes.R_SCORE;

        if (this.state.view === viewTypes.R_SCORE) {
            view = viewTypes.SUMMARY;
        } else {
            gtm.push('Leaderboard', 'Select', 'Round Score');
        }

        this.setState({ view });
    }

    setRound = (roundId) => {
        this.setState({ selectedRoundId: roundId });
    }

    toggleFilterByFavourite = () => {
        if (!this.state.applyFavourite) {
            gtm.push('Leaderboard', 'Favourites', 'Select Favourites');
        }
        this.setState({ applyFavourite: !this.state.applyFavourite });
    }

    filterByFavorite = (playersData) => {
        return playersData.filter((player) =>
            favoritePlayerService.isFavourite(player.id.toString()));
    }

    sortStateInit = (playersData) => {
        const currentStrategy = sortingStrategies[this.state.sortingType];
        return currentStrategy.filteringFunc(playersData, this.state.isSortingAscending);
    }

    processData(players) {
        const { applyFavourite, predictiveSearchResults } = this.state;
        let processedPlayers = players;

        if (applyFavourite) {
            processedPlayers = this.filterByFavorite(processedPlayers);
        }

        if (predictiveSearchResults.length) {
            processedPlayers = processedPlayers.filter((player) => {
                return predictiveSearchResults.find((predictivePlayer) =>
                    predictivePlayer.id === player.id);
            });
        }

        processedPlayers = this.sortStateInit(processedPlayers);

        return processedPlayers;
    }

    sortingHandler = (type) => {
        let isSortingAscending = this.state.isSortingAscending;

        if (this.state.sortingType === type) {
            isSortingAscending = !isSortingAscending;
        } else {
            isSortingAscending = true;
        }

        this.setState({
            sortingType: type,
            isSortingAscending
        });
    }

    renderFavoritePlayers = () => {
        this.setState({
            favoritePlayerList: favoritePlayerService.getAll()
        });
    }

    showPlayerCard = (playerId) => {
        this.setState({ activePlayerId: playerId, isModalOpen: true });
    }

    prepareSuggestions = (players) => {
        const completeSuggestions = getSuggestionList(
            players,
            'id',
            ['sitecoreData.firstName', 'sitecoreData.lastName'],
            (item) => <PlayerSuggestion model={item.sitecoreData} />,
            this.processSearchResults,
            true
        );

        const countries = getSuggestionList(
            players,
            'sitecoreData.country',
            ['sitecoreData.country', 'sitecoreData.countryAliases'],
            (item) => <CountrySuggestion model={item.sitecoreData} />,
            this.processSearchResults
        );

        return completeSuggestions.concat(countries);
    }

    processSearchResults = (filteredDataList, terms) => {
        this.setState({ predictiveSearchResults: filteredDataList });
        if (terms && terms.length > 0) {
            gtm.push('Leaderboard', 'Search', terms.join(' '));
        }
    }

    onSearchToggleState = (isActive) => {
        this.setState({ isPredictive: isActive });
    }

    getCutStatus() {
        const { scoringLeaderboard } = this.props;
        const { currentRound, currentRoundStatus } = scoringLeaderboard;

        if ((currentRound === 2 &&
            (currentRoundStatus !== 5 && currentRoundStatus !== 6)) &&
            scoringLeaderboard.cutLine
        ) {
            return 'projected';
        }

        if ((currentRound > 2 ||
            (currentRound === 2 &&
                (currentRoundStatus === 5 || currentRoundStatus === 6))) &&
            scoringLeaderboard.cutLine
        ) {
            return 'cut';
        }

        return '';
    }

    onInfoButtonClick = () => {
        gtm.push('Leaderboard', 'Information', 'Select Information');
    }

    render() {
        const {
            liveScanner,
            scoringLeaderboard,
            scoringCourse,
            hasChampion,
            options
        } = this.props;
        const { view, roundChoices, selectedRoundId } = this.state;

        let players = this.processData(scoringLeaderboard.players);
        let cutPlayers = [];
        let cutStatus = '';

        if (this.state.sortingType === cellTypes.POSITION &&
            this.state.isSortingAscending &&
            !this.state.predictiveSearchResults.length &&
            !this.state.applyFavourite
        ) {
            cutStatus = this.getCutStatus();

            if (cutStatus) {
                const splittedPlayers = splitPlayers(true, scoringLeaderboard.players, scoringLeaderboard.cutLine);

                players = splittedPlayers[1];
                cutPlayers = splittedPlayers[0];
            }
        }

        const bemList = [this.state.view];

        if (this.props.isPlayOffMode && !hasChampion) {
            bemList.push('play-off');
        }

        if (hasChampion) {
            bemList.push('has-champion');
        }

        const bemCss = generateBemCss('leaderboard', bemList);
        const controlPanelCss = classNames({
            'is-predictive': this.state.isPredictive
        });

        let currentRound = liveScanner.activeRoundId;

        if (view === viewTypes.HOLE_BY_HOLE && this.state.selectedRoundId) {
            currentRound = selectedRoundId;
        }

        return (
            <div className={bemCss}>
                <div className={`leaderboard__control-panel ${controlPanelCss}`}>
                    <div className="leaderboard__controls-panel-col-1">
                        {
                            view === viewTypes.HOLE_BY_HOLE &&
                            <React.Fragment>
                                {
                                    roundChoices.length === 1 &&
                                    <div className="leaderboard__current-round-title single-round">{roundChoices[0].label}</div>
                                }
                                {
                                    roundChoices.length > 1 &&
                                    <div className="leaderboard__select-round">
                                        <LiveEventSelect
                                            choices={roundChoices}
                                            onChange={this.setRound}
                                        />
                                    </div>
                                }
                            </React.Fragment>
                        }
                        <div className="leaderboard__patron ntt-data-logo">
                            <LazyImage image={options.patronLogo} />
                        </div>
                    </div>
                    <div className="leaderboard__controls-panel-col-2">
                        <div className="leaderboard__search-autocomplete">
                            <SearchAutocomplete
                                defaultCallback={
                                    getDefaultCallback(
                                        scoringLeaderboard.players,
                                        [
                                            'sitecoreData.firstName',
                                            'sitecoreData.lastName'
                                        ],
                                        this.processSearchResults
                                    )
                                }
                                suggestions={this.prepareSuggestions(scoringLeaderboard.players)}
                                onToggleState={this.onSearchToggleState}
                            />
                        </div>

                        <FavoriteButton
                            childCss={'leaderboard__favourites'}
                            stateFavoriteButton={this.state.applyFavourite}
                            onClick={this.toggleFilterByFavourite}
                        />

                        <div className="leaderboard__info-button-wrapper">
                            <LeaderboardInfoButton onClick={this.onInfoButtonClick} />
                        </div>

                        <div className="leaderboard__hbh-button-wrapper">
                            <LeaderboardHbhButton
                                view={this.state.view}
                                toggleHoleByHoleView={this.toggleHoleByHoleView}
                            />
                        </div>
                    </div>
                </div>
                {
                    this.state.applyFavourite &&
                    !players.length &&
                    <LivePreMessage model={noFavoriteMessage} />
                }
                {
                    players.length > 0 &&
                    <LeaderboardTableWrapper
                        options={this.props.options}
                        scoringLeaderboard={this.props.scoringLeaderboard}
                        scoringCourse={scoringCourse}
                        currentRound={currentRound}
                        deviceTypeByViewport={app.deviceTypeByViewport}
                        cutStatus={cutStatus}
                        players={players}
                        playersCut={cutPlayers}
                        favoritePlayers={this.state.favoritePlayerList}
                        favoriteState={this.state.applyFavourite}
                        isPlayOffMode={this.props.isPlayOffMode}
                        hasChampion={this.props.hasChampion}
                        view={this.state.view}
                        processingState={{
                            sortingType: this.state.sortingType,
                            isSortingAscending: this.state.isSortingAscending
                        }}
                        toggleRScore={this.toggleRScoreView}
                        sortingHandlerProps={(type) => this.sortingHandler(type)}
                        showPlayerCard={this.showPlayerCard}
                        liveScanner={liveScanner}
                    />
                }
            </div>
        );
    }
}

export default LeaderboardContainer;
